import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  EntityIdentifier,
  getObjectId,
  PipelineFlow,
  PipelineFlowEntity,
  pipelineFlowSelector,
  Quantity,
  useGetPipelineFlowQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {PipelineFlowForm, PipelineFlowFormValues} from "../../components/form/PipelineFlowForm/PipelineFlowForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {usePipelineFlowMutation} from "../../hooks/usePipelineFlowMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<PipelineFlowFormValues>
}

export const PipelineFlowFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <PipelineFlowFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromPipelineFlow = (pipelineFlow: PipelineFlowEntity | undefined): Partial<PipelineFlowFormValues> => {
  let object: EntityIdentifier | undefined = undefined
  let quantity: Quantity | undefined = undefined;

  if (pipelineFlow) {
    if (pipelineFlow.objectId && pipelineFlow.objectType) {
      object = {
        id: pipelineFlow.objectId,
        type: pipelineFlow.objectType,
      }
    }
    if (typeof pipelineFlow.quantity !== 'undefined' && pipelineFlow.unitOfMeasure) {
      quantity = {
        number: pipelineFlow.quantity,
        units: pipelineFlow.unitOfMeasure
      };
    }
  }

  return {
    timeGroup: pipelineFlow?.timeGroup,
    date: adjustFromTimeZoneSafely(pipelineFlow?.dttm),
    object: object,
    quantity: quantity,
    freightType: pipelineFlow?.freightTypeId,
  };
};

export const PipelineFlowFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const selectPipelineFlow = useMemo(() => {
    return pipelineFlowSelector()
  }, [])

  const {currPipelineFlow, isLoading} = useGetPipelineFlowQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currPipelineFlow: selectPipelineFlow(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<PipelineFlowFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromPipelineFlow(currPipelineFlow);
  }, [currPipelineFlow, formDefaults]);

  const title: string = useMemo(() => {
    if (currPipelineFlow && !copy) {
      return t("page.entity.pipelineflow.form.title.edit", "Edit Flow Volume", {ns: "common"});
    } else {
      return t("page.entity.pipelineflow.form.title.new", "New Flow Volume", {ns: "common"});
    }
  }, [currPipelineFlow, copy, t]);

  const {
    createPipelineFlow,
    updatePipelineFlow,
    isCreating,
    isUpdating,
  } = usePipelineFlowMutation()

  const onPipelineFlowSubmit = useCallback((formValues: PipelineFlowFormValues) => {
    const patch: Partial<PipelineFlow> = {
      timeGroup: formValues.timeGroup.code,
      dttm: adjustToTimeZone(formValues.date).getTime(),
      objectId: formValues.object.id,
      objectType: formValues.object.type,
      quantity: formValues.quantity.number,
      unitOfMeasure: formValues.quantity.units,
      freightTypeId: getObjectId(formValues.freightType),
      regionId: regionId,
    }

    if (currPipelineFlow && !copy) {
      const pipelineFlow: Partial<PipelineFlow> = {
        ...currPipelineFlow.asTypeValue(),
        ...patch,
      };
      updatePipelineFlow(pipelineFlow, () => navigate(-1))
    } else {
      const pipelineFlow: Partial<PipelineFlow> = {
        ...patch,
      };
      createPipelineFlow(pipelineFlow, () => navigate(-1))
    }
  }, [regionId, currPipelineFlow, copy, updatePipelineFlow, navigate, createPipelineFlow]);

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <PipelineFlowForm
                  options={{
                    regionId: currPipelineFlow?.regionId ?? regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onPipelineFlowSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

